<template>
  <div class="space-y-4" data-test="wizard-details">
    <div>
      <h3 class="text-base font-semibold text-heading">{{ t('onboarding.wizard.details.title') }}</h3>
      <p class="text-sm text-body">{{ t('onboarding.wizard.details.subtitle') }}</p>
    </div>

    <p v-if="state === 'loading'" class="text-sm text-secondary">{{ t('onboarding.wizard.details.loading') }}</p>

    <!-- Already confirmed (a re-opened wizard): nothing to ask again. -->
    <p v-else-if="state === 'confirmed'" data-test="wizard-details-confirmed" class="text-sm text-body">
      {{ t('onboarding.wizard.details.confirmed', { name: fullName }) }}
    </p>

    <!-- People could not be asked: the step never blocks the wizard on an outage;
         her record stays unconfirmed and HR (or this step, next time) confirms it. -->
    <p v-else-if="state === 'unavailable'" data-test="wizard-details-unavailable" class="text-sm text-secondary">
      {{ t('onboarding.wizard.details.unavailable') }}
    </p>

    <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <Field :label="`${t('onboarding.wizard.details.firstName')} *`">
        <Input v-model="firstName" data-test="wizard-details-first-name" :invalid="showErrors && !firstName.trim()" />
      </Field>
      <Field :label="`${t('onboarding.wizard.details.lastName')} *`">
        <Input v-model="lastName" data-test="wizard-details-last-name" :invalid="showErrors && !lastName.trim()" />
      </Field>
      <p v-if="showErrors && (!firstName.trim() || !lastName.trim())" class="text-xs text-danger-text md:col-span-2">
        {{ t('onboarding.wizard.details.required') }}
      </p>

      <!-- The headquarters, asked with her own name (#1027): it becomes the
           workspace's first location. Only for someone who may write the company
           profile; anyone else sees the section as it always was. -->
      <div v-if="canManageProfile" class="space-y-3 md:col-span-2" data-test="wizard-details-hq">
        <div>
          <h4 class="text-sm font-semibold text-heading">{{ t('onboarding.wizard.details.hq.title') }}</h4>
          <p class="text-sm text-body">{{ t('onboarding.wizard.details.hq.why') }}</p>
        </div>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div class="md:col-span-2">
            <Field :label="`${t('onboarding.wizard.details.hq.street')} *`">
              <Input v-model="hqStreet" data-test="wizard-details-hq-street" :invalid="showErrors && !hqStreet.trim()" />
            </Field>
          </div>
          <Field :label="`${t('onboarding.wizard.details.hq.postalCode')} *`">
            <Input v-model="hqPostalCode" data-test="wizard-details-hq-postal-code" :invalid="showErrors && !hqPostalCode.trim()" />
          </Field>
          <Field :label="`${t('onboarding.wizard.details.hq.city')} *`">
            <Input v-model="hqCity" data-test="wizard-details-hq-city" :invalid="showErrors && !hqCity.trim()" />
          </Field>
          <!-- A real country picker, not free text (D13/A21): the ISO 3166-1
               alpha-2 code goes to hqCountryCode AND the localized display name to
               hqCountry, which downstream (ats-api's projection, the job boards)
               keeps receiving unchanged. Prefilled from the profile, else from the
               signup country when billing knows one. -->
          <Field :label="`${t('onboarding.wizard.details.hq.country')} *`">
            <CountrySelect
              v-model="hqCountryCode"
              :locale="locale"
              data-test="wizard-hq-country"
              :none-label="t('onboarding.wizard.details.hq.countryNone')"
            />
          </Field>
          <p v-if="showErrors && !hqComplete" class="text-xs text-danger-text md:col-span-2" data-test="wizard-details-hq-required">
            {{ t('onboarding.wizard.details.hq.required') }}
          </p>
        </div>
      </div>
      <InlineNotice v-if="saveError" variant="error" class="md:col-span-2" data-test="wizard-details-error">
        {{ saveError }}
      </InlineNotice>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { CountrySelect, countryName, Field, Input, InlineNotice } from '@lumi/ui';
import { useApiMutation } from '@lumi/app-kit';
import { paymentApi, peopleSelfApi, userApi } from '../../utils/api';
import { useOnboardingStore } from '../../stores/onboarding';
import { useTenantsStore } from '../../stores/tenants';

// "Your details" (ADR 0088 §8.3): the owner's Person was created by People's
// bootstrap — unconfirmed, named after what the operator typed or after her
// address. This step is where she says her own name, and saving it CONFIRMS her
// record (`POST /persons/me/confirm`, the one self-confirmation there is, always
// as an employee). Unlike the other sections it writes on Next rather than at
// Finish: it is her own record, not the workspace's configuration, and there is
// nothing to replay. Read and written with the real user's session, never a
// worn demo persona's (`peopleSelfApi`). It cannot be left while her record is unconfirmed — the
// wizard asks every section to `persistComplete` before it moves on.
//
// The headquarters is asked here too (#1027), for someone who may write the
// company profile: people-api turns the first complete one into the workspace's
// first location. It is written with the real user's session just before the
// confirm — except while a demo is live, where the profile and its locations are
// the demo's and Finish wipes them: there it is staged into the wizard's
// `general` blob instead, and the Finish replay writes it into the emptied
// workspace.
const { t, locale } = useI18n();
const tenantsStore = useTenantsStore();
const onboardingStore = useOnboardingStore();

type State = 'loading' | 'unconfirmed' | 'confirmed' | 'unavailable';
const state = ref<State>('loading');
// Suite Home hosts this same section as the owner's prompt (issue 812) and needs
// to know when there is nothing left to ask.
const emit = defineEmits<{ (e: 'state', value: State): void }>();
watch(state, (value) => emit('state', value), { immediate: true });
const firstName = ref('');
const lastName = ref('');
const showErrors = ref(false);

// The headquarters block exists only for someone who may write the company
// profile (People's `settings.manage`, read from the viewer description).
const canManageProfile = ref(false);
const hqStreet = ref('');
const hqPostalCode = ref('');
const hqCity = ref('');
// ISO 3166-1 alpha-2, or '' for "none chosen".
const hqCountryCode = ref('');
const hqComplete = computed(
  () => !!(hqStreet.value.trim() && hqPostalCode.value.trim() && hqCity.value.trim() && hqCountryCode.value.trim()),
);
// The company name the profile already holds: its write is a merge but still
// refuses a body without one.
let profileName = '';

type Headquarters = { hqStreet: string; hqPostalCode: string; hqCity: string; hqCountryCode: string; hqCountry: string };

// The confirm is the section's one write — with the headquarters written just
// before it when it is asked. `inPlace`: a refusal stays beside the
// fields she has to fix, and the wizard stays on this step (persistComplete
// answers false) — a toast that fades would leave her on a step that will not
// let her go without saying why.
const { formError: saveError, mutate: confirmSelf } = useApiMutation<void, [string, string, Headquarters | null]>({
  tier: 'inlineEdit',
  errorChannel: 'inPlace',
  run: async (first, last, hq) => {
    // A retry after a refused confirm rewrites the same values: the profile PUT
    // is a merge, and the location is only ever created once.
    if (hq && !onboardingStore.isDemo) {
      const companyName = profileName || onboardingStore.companyName || tenantsStore.activeTenant?.name || '';
      await peopleSelfApi.put('/tenant/company-profile', { companyName, ...hq });
    } else if (hq) {
      const tenantId = await onboardingStore.activeTenantId();
      if (tenantId) {
        const prior = onboardingStore.staged['general'] || {};
        const res = await userApi.put(`${onboardingStore.base(tenantId)}/staged/general`, {
          companyName: onboardingStore.companyName ?? undefined,
          ...prior,
          ...hq,
        });
        onboardingStore.apply(res.data);
      }
    }
    await peopleSelfApi.post('/persons/me/confirm', { firstName: first, lastName: last });
  },
});

const fullName = computed(() => `${firstName.value} ${lastName.value}`.trim());

// Kept, so Next pressed before the read has answered waits for it instead of
// passing an unconfirmed owner through.
let loaded: Promise<void> = Promise.resolve();
onMounted(() => {
  loaded = load();
});

async function load() {
  try {
    const { data } = await peopleSelfApi.get('/persons/me', { suppressGlobalError: true });
    firstName.value = String(data?.firstName ?? '');
    lastName.value = String(data?.lastName ?? '');
    // Read before the state flips, so the form never appears and then grows.
    if (data?.unconfirmedSince) await loadHeadquarters();
    state.value = data?.unconfirmedSince ? 'unconfirmed' : 'confirmed';
  } catch {
    state.value = 'unavailable';
  }
}

function fillHeadquarters(source: Record<string, unknown>) {
  hqStreet.value = String(source.hqStreet ?? '');
  hqPostalCode.value = String(source.hqPostalCode ?? '');
  hqCity.value = String(source.hqCity ?? '');
  hqCountryCode.value = String(source.hqCountryCode ?? '').trim().toUpperCase();
}

// Best-effort throughout: an answer that cannot be read leaves the block out (or
// empty), never the owner's name unasked.
async function loadHeadquarters() {
  try {
    const { data } = await peopleSelfApi.get('/access/me', { suppressGlobalError: true });
    canManageProfile.value = Array.isArray(data?.permissions) && data.permissions.includes('settings.manage');
  } catch {
    canManageProfile.value = false;
  }
  if (!canManageProfile.value) return;
  if (onboardingStore.isDemo) {
    // The profile on screen is the demo's, not hers: only what she staged counts.
    fillHeadquarters(onboardingStore.staged['general'] || {});
  } else {
    try {
      const { data } = await peopleSelfApi.get('/tenant/company-profile', { suppressGlobalError: true });
      profileName = String(data?.companyName ?? '').trim();
      fillHeadquarters(data ?? {});
    } catch {
      // No readable profile: an empty form, and the save is a merge.
    }
  }
  if (!hqCountryCode.value) await prefillCountryFromSignup();
}

// The signup country payment-api captured at checkout (A20). An actor who is not
// owner / billing_manager / suite_admin gets a 403 from the billing view and a
// tenant created by an operator may have no billing profile at all; neither is
// something to show, the picker simply stays unset.
async function prefillCountryFromSignup() {
  try {
    const res = await paymentApi.get('/billing/overview', { suppressGlobalError: true });
    const code = String(res.data?.profile?.countryCode || '').trim().toUpperCase();
    if (code && !hqCountryCode.value) hqCountryCode.value = code;
  } catch {
    // Deliberately silent — see above.
  }
}

async function persistComplete(): Promise<boolean> {
  await loaded;
  if (state.value !== 'unconfirmed') return true;
  showErrors.value = true;
  const first = firstName.value.trim();
  const last = lastName.value.trim();
  if (!first || !last) return false;
  let hq: Headquarters | null = null;
  if (canManageProfile.value) {
    if (!hqComplete.value) return false;
    const code = hqCountryCode.value.trim().toUpperCase();
    hq = {
      hqStreet: hqStreet.value.trim(),
      hqPostalCode: hqPostalCode.value.trim(),
      hqCity: hqCity.value.trim(),
      hqCountryCode: code,
      hqCountry: countryName(code, locale.value),
    };
  }
  const outcome = await confirmSelf(first, last, hq);
  if (!outcome.ok) return false;
  firstName.value = first;
  lastName.value = last;
  state.value = 'confirmed';
  // The suite-home greeting under the wizard reads this record; closing the wizard
  // after this step (✕, no Finish) must not leave it on the bootstrap name.
  tenantsStore.invalidate();
  return true;
}

// Whether there is still something to ask — read once the record has loaded. The
// wizard asks before it is closed on this step, so leaving it unfilled is never silent.
async function stillUnconfirmed(): Promise<boolean> {
  await loaded;
  return state.value === 'unconfirmed';
}

defineExpose({ persistComplete, stillUnconfirmed });
</script>
